import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.HEP_A,
  displayName: "Hepatitis A",
  serviceType: "administration",
  minimumAgeYears: 1,
  indicationSummary:
    "At risk through travel to endemic areas, sexual behaviour (including MSM), or identified through NaTHNaC assessment.",

  brands: [
    {
      brandId: "havrix-monodose-adult",
      brandName: "Havrix\u00ae Monodose",
      minimumAgeYears: 16,
      route: "IM",
      doseDescription: "1440 ELISA units / 1.0 mL",
      administrationNotes:
        "Shake to a uniform suspension before use. Inspect for particulate matter or discolouration. Administer into the deltoid muscle. Do not use the gluteal region.",
    },
    {
      brandId: "havrix-junior-monodose",
      brandName: "Havrix\u00ae Junior Monodose",
      minimumAgeYears: 1,
      maximumAgeYears: 15,
      route: "IM",
      doseDescription: "720 ELISA units / 0.5 mL",
      administrationNotes:
        "Shake to a uniform suspension before use. Administer into the deltoid muscle. In small infants, administer into the anterolateral thigh.",
    },
    {
      brandId: "avaxim-adult",
      brandName: "AVAXIM\u00ae",
      minimumAgeYears: 16,
      route: "IM",
      doseDescription: "160 ELISA units / 0.5 mL",
      administrationNotes:
        "Shake to a uniform suspension before use. Administer into the deltoid muscle. Do not use the gluteal region.",
    },
    {
      brandId: "avaxim-junior",
      brandName: "AVAXIM\u00ae Junior",
      minimumAgeYears: 1,
      maximumAgeYears: 15,
      route: "IM",
      doseDescription: "80 ELISA units / 0.5 mL",
      administrationNotes:
        "Shake to a uniform suspension before use. Administer into the deltoid muscle. In small infants, administer into the anterolateral thigh.",
    },
    {
      brandId: "vaqta-adult",
      brandName: "VAQTA\u00ae Adult",
      minimumAgeYears: 18,
      route: "IM",
      doseDescription: "50 units / 1.0 mL",
      administrationNotes:
        "Shake to a uniform suspension before use. Inspect before use. Administer into the deltoid muscle.",
    },
    {
      brandId: "vaqta-paediatric",
      brandName: "VAQTA\u00ae Paediatric",
      minimumAgeYears: 1,
      maximumAgeYears: 17,
      route: "IM",
      doseDescription: "25 units / 0.5 mL",
      administrationNotes:
        "Shake to a uniform suspension before use. Administer into the deltoid muscle. In small infants, administer into the anterolateral thigh.",
    },
  ],

  schedules: [
    {
      scheduleId: "hep-a-standard",
      label:
        "Standard (single primary dose; reinforcing dose at 6\u201318 months)",
      offLabel: false,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 primary dose",
        },
        {
          doseNumber: 2,
          label:
            "Reinforcing dose \u2014 6\u201312 months after dose 1 (Havrix\u00ae / AVAXIM\u00ae) or 6\u201318 months after dose 1 (VAQTA\u00ae)",
          intervalFromDose1Days: 180,
        },
      ],
      boosterInfo:
        "A further booster dose may be given approximately 25 years after the reinforcing dose for patients at continued risk.",
      completionNotes:
        "The reinforcing dose gives long-term protection. A delayed reinforcing dose does not require restarting the course. Havrix\u00ae and AVAXIM\u00ae products are interchangeable between doses.",
    },
  ],

  capturesPriorDoseHistory: true,

  exclusions: [
    {
      checkId: "hep-a-ex-anaphylaxis",
      question:
        "Has the patient had a confirmed anaphylactic reaction to a previous hepatitis A vaccine or to any component of hepatitis A vaccines?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Hepatitis A vaccine is contraindicated. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "hep-a-ex-current-infection",
      question:
        "Is there current clinical suspicion of active hepatitis A infection in this patient?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Vaccination is not appropriate where active hepatitis A infection is suspected. Do not vaccinate today.",
      referralMessage:
        "Refer to the patient's GP for investigation and management.",
    },
    {
      checkId: "hep-a-ex-occupation-only",
      question:
        "Is the patient's sole reason for seeking vaccination an occupational risk of hepatitis A (i.e. no travel, sexual behaviour, or other personal risk)?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Occupational vaccination is outside this PGD. Do not supply under this PGD.",
      referralMessage:
        "Direct the patient to their employer's occupational health service.",
    },
  ],

  cautions: [
    {
      checkId: "hep-a-caut-bleeding",
      question:
        "Does the patient have a bleeding disorder or is the patient on anticoagulant therapy?",
      cautionMessage:
        "Administer with care by intramuscular injection. For patients on stable warfarin with INR within therapeutic range, IM injection may proceed using a fine-gauge needle with firm pressure applied for at least two minutes afterwards. Document the clinical decision.",
    },
    {
      checkId: "hep-a-caut-pregnancy",
      question: "Is the patient pregnant or breastfeeding?",
      cautionMessage:
        "Hepatitis A vaccine is inactivated and carries a low theoretical risk in pregnancy. Vaccination should proceed where the risk of hepatitis A infection is high and the benefit outweighs any theoretical risk. Advise the patient to inform their GP or midwife that the vaccine has been given.",
    },
    {
      checkId: "hep-a-caut-immunosuppressed",
      question:
        "Is the patient immunosuppressed due to disease or treatment (e.g. chemotherapy, high-dose steroids, biologics)?",
      cautionMessage:
        "Immunosuppression may reduce the immune response to hepatitis A vaccine. Vaccination should still proceed as the vaccine is inactivated and safe. Counsel that protection may be suboptimal.",
    },
    {
      checkId: "hep-a-caut-incubation",
      question:
        "Could the patient currently be in the incubation period for hepatitis A (20\u201350 days after a potential exposure)?",
      cautionMessage:
        "Unrecognised hepatitis A infection may be present. If active infection is clinically suspected, do not vaccinate. If uncertain, proceed with caution and advise the patient to seek review if symptoms develop.",
    },
    {
      checkId: "hep-a-caut-syncope",
      question:
        "Is there any history of syncope (fainting) following a previous injection or vaccination?",
      cautionMessage:
        "Syncope can occur following vaccination. Ensure the patient is seated or lying down during and for 15 minutes after administration. Do not allow the patient to leave until the observation period is complete.",
    },
  ],

  supplyGates: [],

  requiresICVP: false,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: true,
  requiresGPNotification: true,
  standardObservationMinutes: 15,
  extendedObservationMinutes: 30,

  patientAdvicePoints: [
    "This vaccine protects against hepatitis A only. It does not protect against hepatitis B, hepatitis C, or hepatitis E.",
    "Some protection begins after the first dose. The reinforcing dose (due 6\u201312 months for Havrix\u00ae and AVAXIM\u00ae, or 6\u201318 months for VAQTA\u00ae) is needed for long-term protection.",
    "Continue to follow food and water hygiene advice: drink bottled or boiled water, avoid ice in drinks, eat freshly cooked food, and wash hands thoroughly before eating and after using the toilet.",
    "Men who have sex with men: hepatitis A can be transmitted through sexual contact, including oral\u2013anal contact. Good hygiene during sex helps reduce risk.",
    "If you develop symptoms of hepatitis A (jaundice, dark urine, pale stools, nausea, fever, or abdominal pain) seek medical advice promptly from your GP, NHS 111, or A&E.",
  ],
});
